import { useCallback, useMemo, useState } from 'react';

import { logPackingRecommendationActivity, PackingRecommendationAction } from '../services/packing-activity-service';
import {
  trackPackingRecommendationOverridden,
  trackPackingRecommendationWarningAcknowledged,
} from '../services/packing-recommendations-analytics';
import { PackingEnforcementMode } from './constants';
import { usePackingEnforcementMode } from './use-packing-enforcement-mode';

type PendingOverride = {
  proceed: () => void;
  packageName: string;
  onCancel?: () => void;
};

export type PackingRecommendationGuard = {
  /** Current enforcement mode. Advisory is the only mode where `guardOverride` doesn't already
   * log an Overridden/WarningAcknowledged activity entry before calling `proceed` — callers that
   * also want to log a dismissal should only do so when this is Advisory, to avoid double-logging. */
  mode: PackingEnforcementMode;
  /** True while the Warning enforcement mode's dismissable confirmation is open. */
  isWarningModalOpen: boolean;
  /** True while the Enforced enforcement mode's required-reason modal is open. */
  isOverrideReasonModalOpen: boolean;
  /**
   * Wrap an action that would ship without the recommended package with this before running it.
   * Under Advisory it calls `proceed` immediately. Under Warning it opens the dismissable warning
   * modal and defers `proceed` until confirmed. Under Enforced it opens the required-reason modal
   * and defers `proceed` until a reason is submitted. `onCancel`, when given, runs when the packer
   * cancels out of either modal instead of proceeding (e.g. reverting an already-made edit).
   */
  guardOverride: (proceed: () => void, packageName: string, onCancel?: () => void) => void;
  confirmWarningAndProceed: () => void;
  dismissWarning: () => void;
  submitOverrideReasonAndProceed: (reason: string) => void;
  dismissOverrideReason: () => void;
};

// Shared so Advisory/Warning/Enforced behave identically regardless of which dismiss path calls it.
export const usePackingRecommendationGuard = (fulfillmentPlanId: string): PackingRecommendationGuard => {
  const mode = usePackingEnforcementMode();
  const [pending, setPending] = useState<PendingOverride | null>(null);

  const guardOverride = useCallback(
    (proceed: () => void, packageName: string, onCancel?: () => void) => {
      if (mode === PackingEnforcementMode.Advisory) {
        proceed();
        return;
      }
      setPending({ proceed, packageName, onCancel });
    },
    [mode],
  );

  const confirmWarningAndProceed = useCallback(() => {
    if (!pending) return;
    const { proceed, packageName } = pending;
    setPending(null);
    logPackingRecommendationActivity({ fulfillmentPlanId, action: PackingRecommendationAction.WarningAcknowledged, packageName });
    trackPackingRecommendationWarningAcknowledged(fulfillmentPlanId);
    proceed();
  }, [pending, fulfillmentPlanId]);

  const dismissWarning = useCallback(() => {
    pending?.onCancel?.();
    setPending(null);
  }, [pending]);

  const submitOverrideReasonAndProceed = useCallback(
    (reason: string) => {
      if (!pending) return;
      const { proceed, packageName } = pending;
      setPending(null);
      logPackingRecommendationActivity({
        fulfillmentPlanId,
        action: PackingRecommendationAction.Overridden,
        packageName,
        overrideReason: reason,
      });
      trackPackingRecommendationOverridden(fulfillmentPlanId, mode);
      proceed();
    },
    [pending, fulfillmentPlanId, mode],
  );

  const dismissOverrideReason = useCallback(() => {
    pending?.onCancel?.();
    setPending(null);
  }, [pending]);

  // Memoized so callers using this in a useEffect/useCallback dependency array (e.g. the
  // divergence-detection effect in use-apply-recommendation.tsx) don't re-run on every render —
  // only when something about the guard's actual state or behavior changes.
  return useMemo(
    () => ({
      mode,
      isWarningModalOpen: pending != null && mode === PackingEnforcementMode.Warning,
      isOverrideReasonModalOpen: pending != null && mode === PackingEnforcementMode.Enforced,
      guardOverride,
      confirmWarningAndProceed,
      dismissWarning,
      submitOverrideReasonAndProceed,
      dismissOverrideReason,
    }),
    [mode, pending, guardOverride, confirmWarningAndProceed, dismissWarning, submitOverrideReasonAndProceed, dismissOverrideReason],
  );
};
